import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  TablePagination,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  alpha,
  Collapse,
  Divider,
  Link,
} from '@mui/material';
import { AccountBalance, Receipt, Person, TrendingUp, Logout, ExpandMore, ExpandLess, Info, LocalOffer, CalendarToday, Savings } from '@mui/icons-material';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { getCustomerProfile, getCustomerDebits, getCustomerOutstanding, getCustomerPayments, getCustomerDeposits } from '../../../services/request/Customer';
import { getCustomerProducts, submitInterestRequest, getCustomerInterestRequests, getProduct, Product, InterestRequest } from '../../../services/request/Product';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { toast } from 'react-toastify';
import { CUSTOMER_LOGIN } from '../../../services/constants/URIConstants';
import { ReleasepilotAffiliation } from '../../../shared-components/branding/ReleasepilotAffiliation';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/** Backend may serialize LocalDate as "YYYY-MM-DD" or [y,m,d]. */
function formatDepositDate(value: unknown): string {
  if (value == null) return 'N/A';
  if (Array.isArray(value) && value.length >= 3) {
    const y = Number(value[0]);
    const m = Number(value[1]) - 1;
    const day = Number(value[2]);
    return new Date(y, m, day).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  return 'N/A';
}

const CustomerDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [, , removeCookie] = useCookies(['auth_token', 'user_role']);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [outstanding, setOutstanding] = useState<number>(0);
  const [debits, setDebits] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [debitsPage, setDebitsPage] = useState(0);
  const [debitsRowsPerPage, setDebitsRowsPerPage] = useState(10);
  const [totalDebits, setTotalDebits] = useState(0);
  const [paymentsPage, setPaymentsPage] = useState(0);
  const [paymentsRowsPerPage, setPaymentsRowsPerPage] = useState(10);
  const [totalPayments, setTotalPayments] = useState(0);
  const [deposits, setDeposits] = useState<any[]>([]);
  const [depositsPage, setDepositsPage] = useState(0);
  const [depositsRowsPerPage, setDepositsRowsPerPage] = useState(10);
  const [totalDeposits, setTotalDeposits] = useState(0);
  const [totalActiveDepositsAmount, setTotalActiveDepositsAmount] = useState<number>(0);
  const [depositsLoading, setDepositsLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [productsPage, setProductsPage] = useState(0);
  const [productsRowsPerPage, setProductsRowsPerPage] = useState(12);
  const [totalProducts, setTotalProducts] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [interestMessage, setInterestMessage] = useState('');
  const [interestRequests, setInterestRequests] = useState<InterestRequest[]>([]);
  const [productNames, setProductNames] = useState<Record<string, string>>({});
  const [productDetailDialogOpen, setProductDetailDialogOpen] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  const handleLogout = () => {
    // Clear auth cookies
    const pastDate = new Date();
    pastDate.setDate(1);
    pastDate.setMonth(1);
    pastDate.setFullYear(1960);
    removeCookie('auth_token', { path: '/', expires: pastDate });
    removeCookie('user_role', { path: '/', expires: pastDate });
    
    toast.success('Logged out successfully', { position: 'top-right', autoClose: 2000 });
    
    // Redirect to customer login
    setTimeout(() => {
      navigate(CUSTOMER_LOGIN);
    }, 500);
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (tabValue === 0) {
      fetchDebits();
    } else if (tabValue === 1) {
      fetchPayments();
    } else if (tabValue === 2) {
      fetchDeposits();
    } else if (tabValue === 3) {
      fetchProducts();
    } else if (tabValue === 4) {
      fetchInterestRequests();
    }
  }, [tabValue, debitsPage, debitsRowsPerPage, paymentsPage, paymentsRowsPerPage, depositsPage, depositsRowsPerPage, productsPage, productsRowsPerPage, selectedCategory]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch profile and outstanding in parallel
      const [profileRes, outstandingRes] = await Promise.all([
        getCustomerProfile(),
        getCustomerOutstanding(),
      ]);

      if (profileRes && (profileRes.code === 200 || profileRes.statusCode === 200)) {
        setProfile(profileRes.data);
      }

      if (outstandingRes && (outstandingRes.code === 200 || outstandingRes.statusCode === 200)) {
        setOutstanding(outstandingRes.data?.totalOutstanding || 0);
      }

      // Fetch debits for first tab and deposit summary for summary card
      await Promise.all([fetchDebits(), fetchDepositsSummary()]);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to load dashboard data';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchDebits = async () => {
    try {
      const response = await getCustomerDebits(debitsPage, debitsRowsPerPage, 'ACTIVE');
      if (response && (response.code === 200 || response.statusCode === 200)) {
        const debitsData = response.data;
        // Handle the response structure: data.result contains the debits list
        setDebits(debitsData?.result || debitsData?.debits || []);
        setTotalDebits(debitsData?.totalCount || debitsData?.count || 0);
      } else {
        // If response structure is different, try to extract from response directly
        setDebits(response?.result || response?.debits || []);
        setTotalDebits(response?.totalCount || response?.count || 0);
      }
    } catch (err: any) {
      console.error('Error fetching debits:', err);
      toast.error('Failed to load active debits');
      setDebits([]);
      setTotalDebits(0);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await getCustomerPayments(paymentsPage, paymentsRowsPerPage);
      if (response && (response.code === 200 || response.statusCode === 200)) {
        const paymentsData = response.data;
        // Handle the response structure: data.result contains the payments list
        setPayments(paymentsData?.result || paymentsData?.payments || []);
        setTotalPayments(paymentsData?.totalCount || paymentsData?.count || 0);
      } else {
        // If response structure is different, try to extract from response directly
        setPayments(response?.result || response?.payments || []);
        setTotalPayments(response?.totalCount || response?.count || 0);
      }
    } catch (err: any) {
      console.error('Error fetching payments:', err);
      toast.error('Failed to load payment history');
      setPayments([]);
      setTotalPayments(0);
    }
  };

  const fetchDepositsSummary = async () => {
    try {
      const response = await getCustomerDeposits(0, 1);
      if (response && (response.code === 200 || response.statusCode === 200)) {
        const data = response.data;
        setTotalDeposits(data?.totalCount ?? 0);
        setTotalActiveDepositsAmount(data?.totalActiveAmount ?? 0);
      }
    } catch (err) {
      console.error('Error fetching deposit summary:', err);
    }
  };

  const fetchDeposits = async () => {
    setDepositsLoading(true);
    try {
      const response = await getCustomerDeposits(depositsPage, depositsRowsPerPage);
      if (response && (response.code === 200 || response.statusCode === 200)) {
        const data = response.data;
        setDeposits(data?.result || data?.deposits || []);
        setTotalDeposits(data?.totalCount || data?.count || 0);
        if (data?.totalActiveAmount != null) {
          setTotalActiveDepositsAmount(data.totalActiveAmount);
        }
      } else {
        setDeposits(response?.result || response?.deposits || []);
        setTotalDeposits(response?.totalCount || response?.count || 0);
      }
    } catch (err: any) {
      console.error('Error fetching deposits:', err);
      toast.error('Failed to load deposits');
      setDeposits([]);
      setTotalDeposits(0);
    } finally {
      setDepositsLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDebitsPageChange = (event: unknown, newPage: number) => {
    setDebitsPage(newPage);
  };

  const handleDebitsRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDebitsRowsPerPage(Number.parseInt(event.target.value, 10));
    setDebitsPage(0);
  };

  const handlePaymentsPageChange = (event: unknown, newPage: number) => {
    setPaymentsPage(newPage);
  };

  const handlePaymentsRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentsRowsPerPage(Number.parseInt(event.target.value, 10));
    setPaymentsPage(0);
  };

  const handleDepositsPageChange = (event: unknown, newPage: number) => {
    setDepositsPage(newPage);
  };

  const handleDepositsRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDepositsRowsPerPage(Number.parseInt(event.target.value, 10));
    setDepositsPage(0);
  };

  const fetchProducts = async () => {
    try {
      const response = await getCustomerProducts(productsPage, productsRowsPerPage, selectedCategory === 'ALL' ? undefined : selectedCategory);
      console.log('Customer Products API Response:', response); // Debug log
      if (response) {
        // Response structure: { result: [...], totalCount: number }
        if (response.data.result || response.data.products) {
          setProducts(response.data.result || response.data .products || []);
          setTotalProducts(response.data.totalCount || response.data.count || 0);
        } else if (Array.isArray(response)) {
          // If response is directly an array
          setProducts(response);
          setTotalProducts(response.length);
        }
      }
    } catch (err: any) {
      console.error('Error fetching products:', err);
      toast.error('Failed to load products');
      setProducts([]);
      setTotalProducts(0);
    }
  };

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    setInterestDialogOpen(true);
  };

  const fetchInterestRequests = async () => {
    try {
      const response = await getCustomerInterestRequests();
      let requests: InterestRequest[] = [];
      if (response && (response.code === 200 || response.statusCode === 200)) {
        const requestsData = response.data;
        requests = requestsData?.result || requestsData?.requests || [];
      } else if (response && (response.result || response.requests)) {
        requests = response.result || response.requests || [];
      }
      
      setInterestRequests(requests);
      
      // Fetch product names for all unique product IDs
      const productIds = requests.map(r => r.productId).filter(Boolean) as string[];
      const uniqueProductIds = Array.from(new Set(productIds));
      const productNameMap: Record<string, string> = {};
      
      await Promise.all(
        uniqueProductIds.map(async (productId) => {
          try {
            const productResponse = await getProduct(productId);
            if (productResponse && productResponse.data) {
              productNameMap[productId] = productResponse.data.name || 'Unknown Product';
            }
          } catch (err) {
            console.error(`Error fetching product ${productId}:`, err);
            productNameMap[productId] = 'Unknown Product';
          }
        })
      );
      
      setProductNames(productNameMap);
    } catch (err: any) {
      console.error('Error fetching interest requests:', err);
      toast.error('Failed to load interest requests');
      setInterestRequests([]);
    }
  };

  const handleInterestRequest = async () => {
    if (!selectedProduct) return;
    try {
      // Check if user is authenticated
      const cookies = document.cookie;
      const hasToken = cookies.includes('auth_token=');
      if (!hasToken) {
        toast.error('Please login again to submit interest request');
        return;
      }

      const response = await submitInterestRequest(selectedProduct.id, {
        productId: selectedProduct.id,
        message: interestMessage,
      });
      
      // Check response
      if (response && (response.statusCode === 200 || response.code === 200 || response.data)) {
        toast.success('Interest request submitted successfully! We will contact you soon.');
        setInterestDialogOpen(false);
        setSelectedProduct(null);
        setInterestMessage('');
      } else {
        toast.error(response?.message || 'Failed to submit interest request');
      }
    } catch (err: any) {
      console.error('Error submitting interest request:', err);
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to submit interest request';
      if (errorMsg.includes('Unauthorized') || errorMsg.includes('Invalid token')) {
        toast.error('Session expired. Please login again.');
        // Optionally redirect to login
        setTimeout(() => {
          handleLogout();
        }, 2000);
      } else {
        toast.error(errorMsg);
      }
    }
  };

  if (loading && !profile) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading dashboard...</Typography>
      </Box>
    );
  }

  if (error && !profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* App Bar with Logout */}
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          mb: 3,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: 'white' }}>
              Whatzneu - Customer Portal
            </Typography>
            <ReleasepilotAffiliation compact variant="onDark" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'white', display: { xs: 'none', sm: 'block' } }}>
              {profile?.name || 'Customer'}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Logout />}
              onClick={handleLogout}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  background: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Welcome, {profile?.name || 'Customer'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your account and view your financial information
          </Typography>
        </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Outstanding
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="error.main">
                    {formatCurrency(outstanding)}
                  </Typography>
                </Box>
                <AccountBalance sx={{ fontSize: 40, color: 'error.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Active Debits
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {debits.length}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Payments
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {payments.length}
                  </Typography>
                </Box>
                <Receipt sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Active deposits
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {formatCurrency(totalActiveDepositsAmount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {totalDeposits} record{totalDeposits === 1 ? '' : 's'} total
                  </Typography>
                </Box>
                <Savings sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Account Status
                  </Typography>
                  <Chip
                    label={profile?.creditStatus === 'GOOD' ? 'Active' : 'Inactive'}
                    color={profile?.creditStatus === 'GOOD' ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
                <Person sx={{ fontSize: 40, color: 'info.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Active Debits" />
          <Tab label="Payment History" />
          <Tab label="My Deposits" />
          <Tab label="Products" />
          <Tab label="My Interests" />
          <Tab label="Profile Details" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {loading && debits.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>Loading active debits...</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="right">Principal Amount</TableCell>
                      <TableCell align="right">Outstanding</TableCell>
                      <TableCell>Interest Rate</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>EMI Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {debits.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No active debits found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      debits.map((debit: any) => (
                        <TableRow key={debit.id} hover>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(debit.amount || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="error.main">
                              {formatCurrency(debit.outstanding || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell>{debit.interestRate || 0}%</TableCell>
                          <TableCell>
                            {debit.startDate
                              ? new Date(debit.startDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {debit.endDate
                              ? new Date(debit.endDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })
                              : 'N/A'}
                          </TableCell>
                          <TableCell>{formatCurrency(debit.emiAmount || 0)}</TableCell>
                          <TableCell>
                            <Chip
                              label={debit.status || 'N/A'}
                              color={debit.status === 'ACTIVE' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalDebits}
                page={debitsPage}
                onPageChange={handleDebitsPageChange}
                rowsPerPage={debitsRowsPerPage}
                onRowsPerPageChange={handleDebitsRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {loading && payments.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>Loading payment history...</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Payment Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Received By</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No payment history found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      payments.map((payment: any) => (
                        <TableRow key={payment.id || payment.paymentId} hover>
                          <TableCell>
                            {payment.createdDate
                              ? new Date(payment.createdDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })
                              : payment.createdAt
                              ? new Date(payment.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })
                              : 'N/A'}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {formatCurrency(payment.amount || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={payment.paymentType || 'N/A'}
                              size="small"
                              color={
                                payment.paymentType === 'REPAYMENT'
                                  ? 'success'
                                  : payment.paymentType === 'INTEREST'
                                  ? 'warning'
                                  : payment.paymentType === 'LATE_FEE'
                                  ? 'error'
                                  : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={payment.status || 'N/A'}
                              size="small"
                              color={payment.status === 'APPROVED' ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{payment.paymentReceivedBy || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {payment.note || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalPayments}
                page={paymentsPage}
                onPageChange={handlePaymentsPageChange}
                rowsPerPage={paymentsRowsPerPage}
                onRowsPerPageChange={handlePaymentsRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {depositsLoading && deposits.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>Loading deposits...</Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total active deposit balance:{' '}
                <strong>{formatCurrency(totalActiveDepositsAmount)}</strong>
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Deposit date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Maturity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Source</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deposits.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No deposits found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      deposits.map((row: any) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{formatDepositDate(row.depositDate)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                              {formatCurrency(row.amount || 0, row.currency || 'INR')}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDepositDate(row.maturityDate)}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.status || 'N/A'}
                              size="small"
                              color={row.status === 'ACTIVE' ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                              {row.referenceNumber || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.source || '—'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalDeposits}
                page={depositsPage}
                onPageChange={handleDepositsPageChange}
                rowsPerPage={depositsRowsPerPage}
                onRowsPerPageChange={handleDepositsRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Our Products & Services
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              {['ALL', 'SAAS', 'CLOTHES', 'FINANCIAL_PRODUCTS', 'CONSULTING_SERVICES'].map((cat) => (
                <Chip
                  key={cat}
                  label={cat === 'ALL' ? 'All Products' : cat.replace('_', ' ')}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setProductsPage(0);
                  }}
                  color={selectedCategory === cat ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
          {loading && products.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>Loading products...</Typography>
            </Box>
          ) : products.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No products available at the moment
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={3}>
                {products.map((product) => (
                  <Grid item xs={12} sm={6} md={4} key={product.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: 6,
                        },
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      {product.imageUrl && (
                        <Box
                          component="img"
                          src={product.imageUrl}
                          alt={product.name}
                          sx={{
                            width: '100%',
                            height: 200,
                            objectFit: 'cover',
                          }}
                        />
                      )}
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                          <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
                            {product.name}
                          </Typography>
                          <Chip
                            label={product.category.replace('_', ' ')}
                            size="small"
                            color={
                              product.category === 'SAAS'
                                ? 'primary'
                                : product.category === 'CLOTHES'
                                ? 'secondary'
                                : product.category === 'FINANCIAL_PRODUCTS'
                                ? 'success'
                                : 'warning'
                            }
                          />
                        </Box>
                        {product.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                            {product.description}
                          </Typography>
                        )}
                        {product.price && (
                          <Typography variant="h6" color="primary" fontWeight="bold" sx={{ mb: 2 }}>
                            {formatCurrency(product.price, product.currency)}
                          </Typography>
                        )}
                        
                        {/* Expandable Details Section */}
                        <Box sx={{ mb: 2 }}>
                          <Button
                            size="small"
                            onClick={() => {
                              const newExpanded = new Set(expandedProducts);
                              if (newExpanded.has(product.id)) {
                                newExpanded.delete(product.id);
                              } else {
                                newExpanded.add(product.id);
                              }
                              setExpandedProducts(newExpanded);
                            }}
                            endIcon={expandedProducts.has(product.id) ? <ExpandLess /> : <ExpandMore />}
                            sx={{ textTransform: 'none' }}
                          >
                            {expandedProducts.has(product.id) ? 'Hide Details' : 'Show Details'}
                          </Button>
                        </Box>
                        
                        <Collapse in={expandedProducts.has(product.id)}>
                          <Box sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                            {product.features && (
                              <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Info fontSize="small" color="primary" />
                                  <Typography variant="subtitle2" fontWeight="bold">Features</Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {product.features}
                                </Typography>
                              </Box>
                            )}
                            
                            {product.tags && (
                              <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <LocalOffer fontSize="small" color="secondary" />
                                  <Typography variant="subtitle2" fontWeight="bold">Tags</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {product.tags.split(',').map((tag, idx) => (
                                    <Chip
                                      key={idx}
                                      label={tag.trim()}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                            
                            {product.subscriptionLink && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  Subscription Link
                                </Typography>
                                <Link
                                  href={product.subscriptionLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ wordBreak: 'break-all' }}
                                >
                                  {product.subscriptionLink}
                                </Link>
                              </Box>
                            )}
                            
                            <Divider sx={{ my: 1 }} />
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {product.createdDate && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CalendarToday fontSize="small" color="action" />
                                  <Typography variant="caption" color="text.secondary">
                                    Created: {new Date(product.createdDate).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                    })}
                                  </Typography>
                                </Box>
                              )}
                              {product.updatedDate && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CalendarToday fontSize="small" color="action" />
                                  <Typography variant="caption" color="text.secondary">
                                    Updated: {new Date(product.updatedDate).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                    })}
                                  </Typography>
                                </Box>
                              )}
                              {product.status && (
                                <Box>
                                  <Chip
                                    label={`Status: ${product.status}`}
                                    size="small"
                                    color={product.status === 'ACTIVE' ? 'success' : 'default'}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Collapse>
                        
                        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                          {product.category === 'SAAS' && product.subscriptionLink ? (
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={() => window.open(product.subscriptionLink, '_blank')}
                              sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                },
                              }}
                            >
                              Subscribe Now
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={() => handleProductClick(product)}
                              sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                },
                              }}
                            >
                              Express Interest
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <TablePagination
                  component="div"
                  count={totalProducts}
                  page={productsPage}
                  onPageChange={(e, newPage) => setProductsPage(newPage)}
                  rowsPerPage={productsRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setProductsRowsPerPage(parseInt(e.target.value, 10));
                    setProductsPage(0);
                  }}
                  rowsPerPageOptions={[12, 24, 48]}
                />
              </Box>
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              My Interest Requests
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Track the status of your product interest requests
            </Typography>
          </Box>
          {loading && interestRequests.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>Loading interest requests...</Typography>
            </Box>
          ) : interestRequests.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No interest requests found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You haven't submitted any interest requests yet. Browse products and express your interest!
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: alpha(theme.palette.primary.main, 0.1) }}>
                    <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Submitted Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Last Updated</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {interestRequests.map((request) => {
                    const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
                      switch (status) {
                        case 'INTEREST_RECEIVED':
                          return 'info';
                        case 'ACKNOWLEDGED':
                          return 'warning';
                        case 'CONTACTED':
                          return 'primary';
                        case 'CLOSED':
                          return 'success';
                        default:
                          return 'default';
                      }
                    };

                    const getStatusLabel = (status: string) => {
                      return status?.replace('_', ' ') || 'Unknown';
                    };

                    return (
                      <TableRow key={request.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {productNames[request.productId] || (request.productId ? `Product: ${request.productId.substring(0, 8)}...` : 'N/A')}
                          </Typography>
                          {request.productId && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                              ID: {request.productId.substring(0, 8)}...
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            {request.message || 'No message provided'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {request.createdDate
                              ? new Date(request.createdDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {request.updatedDate
                              ? new Date(request.updatedDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(request.status)}
                            size="small"
                            color={getStatusColor(request.status)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          {profile && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Personal Information</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">Name</Typography>
                      <Typography variant="body1" gutterBottom>{profile.name}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Email</Typography>
                      <Typography variant="body1" gutterBottom>{profile.email || 'N/A'}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Contact Number</Typography>
                      <Typography variant="body1" gutterBottom>{profile.contactNumber || 'N/A'}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Address Information</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">Address</Typography>
                      <Typography variant="body1" gutterBottom>{profile.address || 'N/A'}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>City</Typography>
                      <Typography variant="body1" gutterBottom>{profile.city || 'N/A'}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>State</Typography>
                      <Typography variant="body1" gutterBottom>{profile.state || 'N/A'}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Pincode</Typography>
                      <Typography variant="body1" gutterBottom>{profile.pincode || 'N/A'}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </TabPanel>
      </Paper>

      {/* Interest Request Dialog */}
      <Dialog open={interestDialogOpen} onClose={() => setInterestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Express Interest - {selectedProduct?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {selectedProduct && (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedProduct.description}
                </Typography>
                {selectedProduct.price && (
                  <Typography variant="h6" color="primary" sx={{ my: 2 }}>
                    {formatCurrency(selectedProduct.price, selectedProduct.currency)}
                  </Typography>
                )}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Message (Optional)"
                  placeholder="Tell us about your interest in this product..."
                  value={interestMessage}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInterestMessage(e.target.value)}
                  sx={{ mt: 2 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterestDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleInterestRequest}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              },
            }}
          >
            Submit Interest
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default CustomerDashboard;
